import React, { useContext, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { registerWithAllFields } from '../../../helpers/form-registrations';
import { AppContext } from '../../../Store';
import { DEFAULT_ERROR_CODE } from '../../../helpers/server-messages';
import { useTranslation } from 'react-i18next';
import styles from '../../../css/components/forms/race-select/raceSelectStandard.css';

function RaceSelectStandard( { 
	fieldStates, fieldDispatches,
}) {
	const nicknameState = fieldStates.nicknameState;
	const nicknameDispatch = fieldDispatches.nicknameDispatch;
	const emailState = fieldStates.emailState;
	const emailDispatch = fieldDispatches.emailDispatch;
	const passwordState = fieldStates.passwordState;
	const passwordDispatch = fieldDispatches.passwordDispatch;
	const termsState = fieldStates.termsState;
	const termsDispatch = fieldDispatches.termsDispatch;

	const [state, dispatch] = useContext(AppContext);
	const [shouldRegister, setShouldRegister] = useState(false);
	const [registerAttempted, setRegisterAttempted] = useState(false);
	const { t } = useTranslation('translation');

	const doRegistration = useCallback(async () => {
		if (!registerAttempted) {
			if (nicknameState.error.message ||
				emailState.error.message ||
				passwordState.error.message ||
				termsState.error.message
			) {
				dispatch({ type: 'SET_SERVER_ERROR_MESSAGE', payload: t(DEFAULT_ERROR_CODE) });
				dispatch({ type: 'SHOW_SERVER_ERROR' });
			}
			else {
				nicknameDispatch({ type: 'HIDE_NICKNAME_ERROR' });
				emailDispatch({ type: 'HIDE_EMAIL_ERROR' });
				passwordDispatch({ type: 'HIDE_PASSWORD_ERROR' });
				termsDispatch({ type: 'HIDE_TC_ERROR' });

				setRegisterAttempted(true);
		
				await registerWithAllFields(state.locale, dispatch, nicknameDispatch, passwordDispatch, emailDispatch, termsDispatch,
					nicknameState.value, passwordState.value,
					emailState.value, termsState.value, state.thirdPartyTrackingAccepted, state.race,
				);
			}
		}
	}, [
		dispatch, nicknameDispatch, emailDispatch, passwordDispatch, termsDispatch, state.locale,
		nicknameState, passwordState, emailState, termsState, state.thirdPartyTrackingAccepted, state.race, t, registerAttempted,
	]);
	
	const onElvesSelected = async () => {
		dispatch({ type: 'SET_RACE_TO_ELVES' });
		setShouldRegister(true);
	};
	
	const onHumansSelected = async () => {
		dispatch({ type: 'SET_RACE_TO_HUMANS' });
		setShouldRegister(true);
	};

	useEffect(() => {
		if (shouldRegister && state.race !== null) {
			doRegistration();
		}
	}, [state.race, shouldRegister, doRegistration]);

	return (
		<div className={styles.raceSelectWrapper}>
			<div className={styles.raceSelectBackground}>
				<div className={styles.raceSelectSectionsWrapper}>
					<section className={`${styles.raceSelectSection} ${styles.humans}`}>
						<h1 className={styles.raceSelectTitle}>{t('raceSelection.humans.title')}</h1>
						<button
							className={styles.raceSelectCtaButton}
							onClick={onHumansSelected}
							data-testid="race-selection-humans"
						>
							{t('raceSelection.cta')}
						</button>
						<p className={styles.raceSelectText}>{t('raceSelection.humans.text')}</p>
					</section>
					<section className={`${styles.raceSelectSection} ${styles.elves}`}>
						<h1 className={styles.raceSelectTitle}>{t('raceSelection.elves.title')}</h1>
						<button
							className={styles.raceSelectCtaButton}
							onClick={onElvesSelected}
							data-testid="race-selection-elves"
						>
							{t('raceSelection.cta')}
						</button>
						<p className={styles.raceSelectText}>{t('raceSelection.elves.text')}</p>
					</section>
				</div>
			</div>
		</div>
	);
}

const fieldStatePropType = PropTypes.shape({
	value: PropTypes.oneOfType([ PropTypes.string, PropTypes.bool]),
	error: PropTypes.shape({
		message: PropTypes.string,
		show: PropTypes.bool,
	}),
	valid: PropTypes.bool || null,
});

RaceSelectStandard.propTypes = {
	fieldStates: PropTypes.shape({
		nicknameState: fieldStatePropType,
		passwordState: fieldStatePropType,
		emailState: fieldStatePropType,
		termsState: fieldStatePropType,
	}).isRequired,
	fieldDispatches: PropTypes.shape({
		nicknameDispatch: PropTypes.func,
		passwordDispatch: PropTypes.func,
		emailDispatch: PropTypes.func,
		termsDispatch: PropTypes.func,
	}).isRequired,
};

export default RaceSelectStandard;
